//Utils
import {isObjectEmpty, isValidArray} from './common-utils';
import {prepareRoomStayCandidatesCG} from './prepare-cg-pax-data';
import parseUrlDateString from './date-utils/parse-url-date-string';
import {dateTransformUtil, setCheckInCheckout} from './date-utils/date-utils';
import splitRoomString from './split-room-string';
import pluralise from './common-pluralise-if-necessary';

//types
import {SearchDetailsForCrossRecentSearch} from '@stencil/hotels-types';
import {NativeRecentSearchUIAdaptor} from '@stencil/hotels-types/src/common/types';

export const recentSearchCardDateTransform = <T>(checkIn: T, checkOut: T) => {
  return {
    dateString: `${dateTransformUtil(checkIn, 'dd mmm')} - ${dateTransformUtil(
      checkOut,
      'dd mmm',
    )}`,
    rsCheckin: dateTransformUtil(checkIn, 'dd-mm-yyyy HH:MM:ss'),
    rsCheckout: dateTransformUtil(checkOut, 'dd-mm-yyyy HH:MM:ss'),
  };
};

export const goDataQdDatePaxParser = (qd: string) => {
  if (!qd) return null;
  const [checkIn, checkOut, ...roomString] = qd.split('-');
  return {
    checkIn: parseUrlDateString(checkIn),
    checkOut: parseUrlDateString(checkOut),
    roomString: roomString.join('-'),
  };
};

export const setCommonsRecentSearchData = ({
  tagId,
  godata,
  name,
  adultCount,
  childCount,
  childAgeArray,
  roomCount,
  title,
  lobName,
  traveldate,
  returnDate,
  url,
  subLob = '',
  hotelId = '',
  isInternational,
  countryCode,
  cityName,
  cityId,
  type,
  roomString,
  locus,
}) => {
  const {dateString, rsCheckin, rsCheckout} = recentSearchCardDateTransform(traveldate, returnDate);

  const pax = prepareRoomStayCandidatesCG(roomString);

  /* @Note this is done for parity with types of recent search pax as required by backend (Recommendation)
   */

  const rsPax = pax.reduce((accumulator, paxItem, index) => {
    const childCount = !!paxItem?.childAges?.length ? paxItem?.childAges?.length : 0;

    accumulator.push({
      count: Number(paxItem?.adultCount + childCount),
      details: {
        adult: {
          count: paxItem?.adultCount,
        },
        child: {
          count: childCount,
          ages: paxItem?.childAges,
        },
      },
    });

    return accumulator;
  }, []);

  let data = {
    searchContext: {
      toDateTime: {
        ts: returnDate?.getTime(),
        zone: 'IST',
        str: rsCheckout,
      },
      lob: lobName,
      isDSD: true,
      pax: rsPax,
      funnelSource: subLob,
      lobCategory: isInternational ? 'IH' : 'DH',
      journeyType: 'OW',
      product: {
        id: hotelId || cityId,
        name,
      },
      to: {
        // only to no from field
        cityName,
        name: name,
        lobCity: cityId,
        countryCode: countryCode,
        lobCountry: countryCode,
        voyager: {
          id: cityId,
          hotelVid: hotelId,
        },
        locus: {
          country: countryCode,
          region: locus?.cityCode,
          name: cityName,
          city: locus?.cityCode,
          type: locus?.type,
          area: [cityName],
        },
      },
      fromDateTime: {
        ts: traveldate?.getTime(),
        zone: 'IST',
        str: rsCheckin,
      },
      meta: {
        title: title,
        subtitle: dateString,
        deeplink: url,
        tg: tagId,
        gd: JSON.stringify(godata),
      },
    },
  } as SearchDetailsForCrossRecentSearch;

  return data;
};

export const setOldMwebCommonsRecentSearchData = ({
  tagId,
  godata,
  userId,
  destination,
  id,
  name,
  paxCount,
  roomCount,
  roomType,
  title,
  source,
  lobName,
  traveldate,
  returnDate,
  url,
  subLob = '',
}) => {
  let data = {
    tagId,
    godata,
    userId,
    destination,
    id,
    name,
    paxCount,
    roomCount,
    roomType,
    title,
    source,
    lobName,
    traveldate,
    returnDate,
    url,
    subLob,
  };
  return data;
};

export function calculateSkyWalkerPaxValue(paxArray) {
  return paxArray.reduce((total, pax) => total + pax.count, 0);
}

export function cleanAndMergeRsEntries(entries) {
  const now = new Date();
  const merged = {};

  for (const entry of entries) {
    const key = entry.destination?.n?.toLowerCase();
    if (!key) continue; // Skip if no valid destination name

    // Determine checkin date and checkout date
    let checkinDate = entry.checkin?.date ? new Date(entry.checkin.date) : null;
    let checkoutDate;
    if (entry.isDayUse && checkinDate) {
      // Day-use scenario: treat checkout date the same as checkin date
      checkoutDate = checkinDate;
    } else if (entry.checkout?.date) {
      checkoutDate = new Date(entry.checkout.date);
    }

    // Check if either checkin or checkout is in the past.
    // If checkin date is in the past (earlier than now), skip.
    if ((checkinDate && checkinDate < now) || (checkoutDate && checkoutDate < now)) {
      continue;
    }
    // Merge logic
    if (!merged[key]) {
      merged[key] = {...entry};
    } else {
      merged[key] = {
        ...merged[key],
        checkin: merged[key].checkin || entry.checkin, // Use existing checkin if available, since we are picking the latest
        checkout: merged[key].checkout || entry.checkout,
        roomString: merged[key].roomString || entry.roomString,
        // Add other fields as needed
      };
    }
  }

  return Object.values(merged);
}

export const mapSkyWalkerFunnelData = ({
  funnel = {},
  goHomeSearch = {},
  isCross = false,
}: {
  funnel?: any;
  goHomeSearch?: any;
  isCross?: boolean;
}) => {
  let roomStringForUI = '';
  let dateForUI = '';
  let guestCount = 0;
  let roomCount = 0;
  let reactSearchObj = {};

  if (isCross && funnel && !isObjectEmpty(funnel)) {
    roomCount = funnel?.rooms;
    if (isValidArray(funnel?.pax)) {
      guestCount = calculateSkyWalkerPaxValue(funnel.pax);
      roomStringForUI = `${guestCount} | ${roomCount} ${pluralise('Rooms', roomCount)}`;
    } else {
      roomStringForUI = `${roomCount} ${pluralise('Room', roomCount)}`; // Fallback if pax is not an array
    }
    dateForUI = funnel?.meta?.subtitle || ''; // Ensure a fallback for date

    reactSearchObj = {
      badgeText: funnel?.funnelSource || '',
      title: funnel?.meta?.title || '',
      ctaUrl: funnel?.meta?.deeplink || '',
      gd: funnel?.meta?.gd || {},
      subLob: funnel?.funnelSource || '',
      lob: funnel?.lob || 'hotels',
    };
  } else if (!isObjectEmpty(goHomeSearch)) {
    const {paxCount, roomCount: roomValue} = splitRoomString(goHomeSearch?.roomString);
    roomCount = roomValue;
    guestCount = paxCount;
    roomStringForUI = `${guestCount} | ${roomCount} ${pluralise('Room', roomCount)}`;

    const {checkinDateForUI, checkoutDateForUI} = setCheckInCheckout(
      goHomeSearch.checkin.date,
      goHomeSearch.checkout.date,
    );

    dateForUI = `${dateTransformUtil(checkinDateForUI, 'd mmm')} - ${dateTransformUtil(checkoutDateForUI, 'd mmm')}`;

    const searchData = {
      ...goHomeSearch,
      date: {checkinDateForUI, checkoutDateForUI},
    };

    reactSearchObj = {
      badgeText: goHomeSearch?.destination?.t || '',
      title: goHomeSearch?.destination?.n || '',
      ctaUrl: '',
      gd: searchData,
      subLob: goHomeSearch?.destination?.t || '',
      lob: 'hotels',
    };
  }

  return {
    ...reactSearchObj,
    paxText: roomStringForUI || 'N/A', // Fallback if roomStringForUI is empty
    date: dateForUI || 'N/A', // Fallback if dateForUI is empty
    guestCount: guestCount || 0,
    roomCount: roomCount || '',
  } as NativeRecentSearchUIAdaptor;
};

interface RecentSearchAdaptorOptions {
  skywalkerData?: any;
  goHomeSearches?: any[];
  isCrossRs: boolean;
}

export const recentSearchAdaptor = ({
  skywalkerData = {},
  goHomeSearches = [],
  isCrossRs = false,
}: RecentSearchAdaptorOptions) => {
  let recentSearchData = null;

  const skyWalkerTransformedData = Object.values(skywalkerData || {});

  if (isCrossRs && isValidArray(skyWalkerTransformedData)) {
    recentSearchData = skyWalkerTransformedData.map(item =>
      mapSkyWalkerFunnelData({funnel: item, isCross: true}),
    );
  } else if (isValidArray(goHomeSearches)) {
    recentSearchData = goHomeSearches.map(item => mapSkyWalkerFunnelData({goHomeSearch: item}));
  }
  return {recentSearchData};
};

export const checkIfRecentSearchisHotelGoSuggest = ({selectedItem}) => {
  return selectedItem?.xtr?.type === 'hotel' || selectedItem?.t === 'hotel';
};
